
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { JobApplication, ApplicationStatus } from "@/types/application";
import { toast } from "sonner";
import { trackApplicationStatusChanged } from "@/utils/analytics/jobTracking";

interface UpdateApplicationStatusVariables {
  applicationId: string;
  jobId: string;
  newStatus: ApplicationStatus;
  previousStatus?: ApplicationStatus;
  jobTitle?: string;
  candidateId?: string;
}

interface JobApplicationWithRelations extends JobApplication {
  job?: {
    id: string;
    title: string;
  };
  candidate?: {
    id: string;
    first_name: string;
    last_name: string;
  };
}

async function updateApplicationStatusInDb({ applicationId, newStatus }: UpdateApplicationStatusVariables): Promise<JobApplicationWithRelations> {
  const { data, error } = await supabase
    .from("job_applications")
    .update({ status: newStatus, updated_at: new Date().toISOString() })
    .eq("id", applicationId)
    .select(`
      *,
      candidate:candidates(id, first_name, last_name),
      job:jobs(id, title)
    `)
    .single();

  if (error) {
    console.error("Error updating application status:", error.message);
    toast.error(`Failed to update status: ${error.message}`);
    throw new Error(`Failed to update application status: ${error.message}`);
  }

  if (!data) {
    toast.error("Failed to update status: No data returned.");
    throw new Error("Failed to update application status: No data returned.");
  }
  
  return data as JobApplicationWithRelations;
}

export function useUpdateApplicationStatus() {
  const queryClient = useQueryClient();

  return useMutation<JobApplicationWithRelations, Error, UpdateApplicationStatusVariables>({
    mutationFn: updateApplicationStatusInDb,
    onSuccess: (data, variables) => {
      // Invalidate the specific job applications query to refetch
      queryClient.invalidateQueries({ queryKey: ["jobApplications", variables.jobId] });
      toast.success(`Application status updated to ${variables.newStatus.replace('_', ' ')}.`);
      
      // Track application status change
      if (data.job && data.candidate) {
        trackApplicationStatusChanged({
          job_id: variables.jobId,
          candidate_id: data.candidate.id,
          job_title: data.job.title,
          from_status: variables.previousStatus,
          to_status: variables.newStatus
        });
      }
    },
    onError: (error) => {
      console.error("Mutation error in useUpdateApplicationStatus:", error.message);
    },
  });
}
